import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RunButton, ActionButton, useLoad, fmt, label } from "./kit";
import { FilterEmpty } from "./v3";

const DETAIL = {
  review: "/profilpruefungen", report: "/meldungen-sicherheit", activity: "/aktivitaeten", access: "/aktivitaeten",
  ticket: "/support", organization: "/organisationen",
};
export const taskTarget = (t) => (DETAIL[t.kind] ? `${DETAIL[t.kind]}/${t.id}` : "/einstellungen?tab=daten");
const target = taskTarget;

const FIXED_PRIORITY = {
  report: "Die Dringlichkeit dieser Meldung wird in der Meldung selbst festgelegt.",
  ticket: "Die Dringlichkeit dieses Support-Falls wird im Fall selbst festgelegt.",
};

// C32: assignment is its own page (`/aufgaben/aktion/zuweisen-<kind>-<id>`); assignees load only when it is open.
function AssignButton({ task, onDone }) {
  const { aktion } = useParams();
  const page = `zuweisen-${task.kind}-${task.id}`;
  const st = useLoad(aktion === page ? `/tasks/${task.kind}/assignees` : null);
  const options = (st.data?.assignees || []).map((a) => ({ value: a.id, label: `${a.name} (${label(a.role)})` }));
  const prios = st.data?.priorities || [];
  const priorityField = st.data?.priority_editable
    ? [{ name: "priority", label: "Priorität", type: "select", placeholder: false,
        default: prios.includes(task.priority) ? task.priority : (prios.includes("normal") ? "normal" : prios[0]),
        options: prios.map((p) => ({ value: p, label: label(p) })) }]
    : [{ type: "info", text: FIXED_PRIORITY[task.kind] || "Die Priorität dieses Vorgangs wird im Fall selbst festgelegt." }];
  if (aktion === page && !st.data) return null;
  return (
    <ActionButton label="Zuweisen" testId={`task-assign-${task.id}`} page={page} title="Aufgabe zuweisen"
        pageSubtitle="Weise die Aufgabe einer fachlich berechtigten Person zu." pageSubject={`${task.label}: ${task.title || "–"}`}
        pageRows={[["Aufgabe", `${task.label}: ${task.title || "–"}`]]}
        description="Es werden nur Personen mit passender Berechtigung angezeigt."
        fields={[
          { name: "user_id", label: "Zuständige Person", type: "select", options, required: true },
          { name: "reason", label: "Begründung", type: "textarea", required: true, help: "Wird im Verlauf gespeichert." },
          ...priorityField,
          { name: "note", label: "Interne Notiz (optional)", type: "textarea", help: "Nur für das Team sichtbar, im Verlauf des Vorgangs." },
        ]}
        submitLabel="Zuweisung prüfen" onDone={onDone}
        onSubmit={async (v) => { await api.post(`/tasks/${task.kind}/${task.id}/assign`, v); return "Aufgabe zugewiesen."; }} />
  );
}

function TaskRow({ t, scope, reload }) {
  return (
    <li data-testid={`task-${t.kind}-${t.id}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t.area} · {t.label}</p>
        <Link to={target(t)} className="font-semibold text-brand-purple hover:underline">{t.title || "Ohne Titel"}</Link>
        <p className="text-xs text-muted-foreground">
          Status: {label(t.status)} · {t.assigned_to ? `Zuständig: ${t.assigned_to.name}` : "Nicht zugewiesen"} · seit {fmt(t.created_at)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Status value={t.priority} />
        {!t.assigned_to && scope !== "done" && (
          <RunButton label="Übernehmen" testId={`task-claim-${t.id}`} success="Aufgabe übernommen." onDone={reload}
            run={() => api.post(`/tasks/${t.kind}/${t.id}/assign`, {})} />
        )}
        {scope !== "done" && <AssignButton task={t} onDone={reload} />}
      </div>
    </li>
  );
}

export function TaskPanel({ initialScope = "open", country = "all" }) {
  const [scope, setScope] = useState(initialScope);
  const st = useLoad(`/tasks?scope=${scope}&country=${country}`);
  const tabs = useTabs([
    { value: "open", label: "Offen für mich" }, { value: "mine", label: "Mir zugewiesen" }, { value: "done", label: "Von mir erledigt" },
  ], scope);
  return (
    <Section title={<span className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-brand-magenta" /> Meine Aufgaben</span>} testId="task-panel">
      <Tabbar tabs={tabs} onChange={setScope} testId="task-scope" />
      <TabPanel tabs={tabs} testId="task-scope-panel">
        <Loadable state={st}>
          {(d) => (d.tasks.length === 0 ? (scope === "open" && country === "all" ? <Empty text="Keine offenen Aufgaben." />
            : <FilterEmpty filters={[["Ansicht", tabs.items[tabs.index].label]]} onReset={() => setScope("open")} testId="task-filter-empty" />) : (
            <ul className="divide-y divide-border" data-testid="task-list">
              {d.tasks.map((t) => <TaskRow key={`${t.kind}-${t.id}`} t={t} scope={scope} reload={st.reload} />)}
            </ul>
          ))}
        </Loadable>
      </TabPanel>
    </Section>
  );
}
